import { z } from 'zod';
import { validateEan13Checksum } from './normalize.js';

// ---------- Shared primitives ----------

// EAN13 is now NULLABLE on the canonical product (TD-0010): EAN-less products
// join via a later criterion. It is no longer the product key — `productId` is.
export const EAN13 = z.string().regex(/^\d{13}$/);

// Canonical product id (catalog.product.product_id). Always present; the detail
// route and suggest redirect key on this, not on EAN13.
export const ProductId = z.string().min(1);

// CLP money. product_flat carries best_price/list_price as NUMERIC; Chilean
// pesos are integer-valued but we keep the response permissive (no .int()) so a
// stray NUMERIC scale never fails response shaping.
export const Money = z.number().nonnegative();

export const IsoTs = z.string().datetime({ offset: true });

// HTTPS-only URL primitive. Product image URLs are served from the public GCS
// bucket; the https refinement guards against a mixed-content http:// fallback.
export const HttpsUrl = z
  .string()
  .url()
  .refine((s) => s.startsWith('https://'), { message: 'must be https' });

// ---------- Error envelope ----------

export const ErrorCode = z.enum([
  'unauthorized',
  'forbidden',
  'not_found',
  'validation',
  'conflict',
  'rate_limited',
  'internal',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ApiError = z.object({
  error: z.object({ code: ErrorCode, message: z.string() }),
});
export type ApiError = z.infer<typeof ApiError>;

// ---------- /health ----------

export const HealthResponse = z.object({
  status: z.literal('ok'),
  version: z.string(),
});
export type HealthResponse = z.infer<typeof HealthResponse>;

// Deep health: catalog reachability + product_flat freshness from refreshed_at
// (AC7). No Firestore — the static allowlist replaces the whitelist collection.
export const HealthDeepResponse = z.object({
  status: z.literal('ok'),
  bq: z.literal('ok'),
  productFlatRefreshedAt: IsoTs,
  maxObservedAt: IsoTs.nullable(),
  freshnessLagSeconds: z.number().int().nullable(),
});
export type HealthDeepResponse = z.infer<typeof HealthDeepResponse>;

// ---------- /me ----------

// Static 2-user allowlist (D5): no roles, no Firestore binding, no claim drift.
export const MeResponse = z.object({
  email: z.string().email(),
});
export type MeResponse = z.infer<typeof MeResponse>;

// ---------- /search/suggest ----------

export const SuggestItem = z.object({
  productId: ProductId,
  ean13: EAN13.nullable(),
  name: z.string(),
  brand: z.string().nullable(),
});
export type SuggestItem = z.infer<typeof SuggestItem>;

export const SuggestResponse = z.object({
  items: z.array(SuggestItem),
  hasMore: z.boolean(),
  // Redirect keys on productId — the detail route is /product/:productId.
  redirect: z.object({ productId: ProductId }).nullable(),
});
export type SuggestResponse = z.infer<typeof SuggestResponse>;

// ---------- Price model (richer than the prototype's single `precio`) ----------

// Currently-valid conditional offer (now within valid_from/valid_to). Complementary
// info only — never feeds best_price. All fields nullable: the upstream offer rows
// are heterogeneous across retailers.
export const Offer = z.object({
  offerType: z.string().nullable(),
  unitPrice: Money.nullable(),
  totalPrice: Money.nullable(),
  minQty: z.number().int().nullable(),
  paidQty: z.number().int().nullable(),
  paymentCondition: z.string().nullable(),
  membershipCondition: z.string().nullable(),
  promoTag: z.string().nullable(),
  promoName: z.string().nullable(),
  validTo: IsoTs.nullable(),
});
export type Offer = z.infer<typeof Offer>;

// One entry per retailer carrying the product. `prices[]` arrives cheapest-first
// (best_price ASC) from the refresh job, so the SPA's "lowest" is `prices[0]`.
export const RetailerPrice = z.object({
  retailerId: z.string().nullable(),
  retailerName: z.string().nullable(),
  bestPrice: Money.nullable(),
  listPrice: Money.nullable(),
  available: z.boolean().nullable(),
  observedAt: IsoTs.nullable(),
  offers: z.array(Offer),
});
export type RetailerPrice = z.infer<typeof RetailerPrice>;

// ---------- /search/products ----------

export const ProductRow = z.object({
  productId: ProductId,
  ean13: EAN13.nullable(),
  name: z.string(),
  brand: z.string().nullable(),
  packaging: z.string().nullable(),
  imageUrl: HttpsUrl.nullable(),
  category: z.object({ id: z.string(), label: z.string() }).nullable(),
  prices: z.array(RetailerPrice),
});
export type ProductRow = z.infer<typeof ProductRow>;

export const Facet = z.object({
  id: z.string(),
  label: z.string(),
  count: z.number().int().nonnegative(),
});
export type Facet = z.infer<typeof Facet>;

export const ProductsResponse = z.object({
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.literal(25),
  results: z.array(ProductRow),
  facets: z.object({
    brands: z.array(Facet),
  }),
});
export type ProductsResponse = z.infer<typeof ProductsResponse>;

// ---------- /products/:productId ----------

// nutrition_facts is upstream JSON (deferred population, NULL today). The detail
// handler parses it best-effort into rows; all fields tolerated as strings.
export const NutritionRow = z.object({
  nutrient: z.string(),
  value: z.string(),
  unit: z.string(),
});
export type NutritionRow = z.infer<typeof NutritionRow>;

export const ProductDetail = z.object({
  productId: ProductId,
  ean13: EAN13.nullable(),
  name: z.string(),
  brand: z.string().nullable(),
  category: z.object({ id: z.string(), label: z.string() }).nullable(),
  packaging: z.string().nullable(),
  imageUrl: HttpsUrl.nullable(),
  description: z.string().optional(),
  nutritionFacts: z.array(NutritionRow),
  prices: z.array(RetailerPrice),
});
export type ProductDetail = z.infer<typeof ProductDetail>;

// ---------- Query param schemas (server-side) ----------

const CsvList = z
  .string()
  .optional()
  .transform((s) =>
    s
      ? s
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean)
      : [],
  )
  .refine((arr) => arr.length <= 50, { message: 'too many filters (max 50)' });

export const SuggestQuery = z.object({
  q: z
    .string()
    .transform((s) => s.normalize('NFC').trim())
    .refine((s) => s.length >= 4, { message: 'q must be at least 4 chars' })
    .refine((s) => s.length <= 100, { message: 'q too long' }),
});

export const ProductsQuery = z.object({
  q: z
    .string()
    .transform((s) => s.normalize('NFC').trim())
    .refine((s) => s.length <= 100, { message: 'q too long' })
    .optional(),
  brands: CsvList,
  page: z.coerce.number().int().min(1).default(1),
});

// ---------- /scan/unknown ----------

export const ScanUnknownRequest = z
  .object({
    ean13: z
      .string()
      .regex(/^\d{13}$/, 'ean13 must be 13 digits')
      .refine(validateEan13Checksum, 'invalid mod-10 checksum'),
  })
  .strict();
export type ScanUnknownRequest = z.infer<typeof ScanUnknownRequest>;

// ---------- Product grouping (TUC-data-0005 / issue #196) ----------
// The human-confirmed GTIN-equivalence layer. The internal app moves read-only
// -> read/write here: staff confirm/reject engine candidates, manually create
// groups, and resolve flagged groups. Every write is attributed to the actor.

// A candidate proposed by the engine: >=2 customer-equivalent GTINs sharing the
// conservative exact key, with the confidence signals that rank the review queue.
export const GroupCandidate = z.object({
  candidateKey: z.string().min(1),
  memberEan13s: z.array(EAN13).min(2),
  brandNorm: z.string().nullable(),
  nameSample: z.string().nullable(),
  netContent: z.string().nullable(),
  unit: z.string().nullable(),
  retailerCount: z.number().int().nonnegative(),
  priceCv: z.number().nullable(),
  nameTokenCount: z.number().int().nonnegative(),
  confidence: z.number().nullable(),
  rank: z.number().int().positive(),
});
export type GroupCandidate = z.infer<typeof GroupCandidate>;

export const GroupCandidatesResponse = z.object({
  items: z.array(GroupCandidate),
});
export type GroupCandidatesResponse = z.infer<typeof GroupCandidatesResponse>;

// One member's per-retailer offer for the side-by-side review (live page link).
export const GroupMemberOffer = z.object({
  retailerId: z.string().nullable(),
  retailerName: z.string().nullable(),
  bestPrice: Money.nullable(),
  available: z.boolean().nullable(),
  url: z.string().nullable(),
});
export type GroupMemberOffer = z.infer<typeof GroupMemberOffer>;

export const GroupMember = z.object({
  ean13: EAN13,
  productId: ProductId.nullable(),
  name: z.string().nullable(),
  brand: z.string().nullable(),
  netContent: z.string().nullable(),
  unit: z.string().nullable(),
  imageUrl: z.string().nullable(),
  offers: z.array(GroupMemberOffer),
});
export type GroupMember = z.infer<typeof GroupMember>;

// Full review payload: the candidate + each member resolved (names/prices/images
// + live retailer-page deep-links) for the side-by-side check.
export const GroupCandidateDetail = z.object({
  candidateKey: z.string().min(1),
  brandNorm: z.string().nullable(),
  netContent: z.string().nullable(),
  unit: z.string().nullable(),
  confidence: z.number().nullable(),
  retailerCount: z.number().int().nonnegative(),
  priceCv: z.number().nullable(),
  nameTokenCount: z.number().int().nonnegative(),
  members: z.array(GroupMember),
});
export type GroupCandidateDetail = z.infer<typeof GroupCandidateDetail>;

// A persisted group + its members (manual-create result / flagged-queue row).
export const Group = z.object({
  groupId: z.string().min(1),
  status: z.enum(['active', 'flagged', 'dissolved']),
  provenance: z.enum(['candidate-confirmed', 'manual']),
  confidence: z.number().nullable(),
  netContent: z.string().nullable(),
  unit: z.string().nullable(),
  createdBy: z.string(),
  members: z.array(GroupMember),
});
export type Group = z.infer<typeof Group>;

export const FlaggedGroupsResponse = z.object({
  items: z.array(Group),
});
export type FlaggedGroupsResponse = z.infer<typeof FlaggedGroupsResponse>;

// ----- write requests -----

// Manual create: staff-picked GTINs (>=2). Same-size + exclusivity are enforced
// server-side against catalog.product / product_group_member.
export const CreateGroupRequest = z
  .object({
    ean13s: z.array(EAN13).min(2),
    note: z.string().max(500).optional(),
  })
  .strict();
export type CreateGroupRequest = z.infer<typeof CreateGroupRequest>;

export const CreateGroupResponse = z.object({ group: Group });
export type CreateGroupResponse = z.infer<typeof CreateGroupResponse>;

// One decision in a (possibly bulk) confirm/reject action. `confirm` materializes
// a group from the candidate's members; `reject` records a durable verdict that
// suppresses re-raise. Multi-select bulk = an array of these.
export const CandidateDecision = z
  .object({
    candidateKey: z.string().min(1),
    verdict: z.enum(['confirm', 'reject']),
    note: z.string().max(500).optional(),
  })
  .strict();
export type CandidateDecision = z.infer<typeof CandidateDecision>;

export const DecideRequest = z
  .object({
    decisions: z.array(CandidateDecision).min(1).max(200),
  })
  .strict();
export type DecideRequest = z.infer<typeof DecideRequest>;

// Per-decision outcome, so a bulk action reports each item individually.
export const DecisionOutcome = z.object({
  candidateKey: z.string().min(1),
  verdict: z.enum(['confirm', 'reject']),
  ok: z.boolean(),
  groupId: z.string().nullable(),
  error: z.string().nullable(),
});
export type DecisionOutcome = z.infer<typeof DecisionOutcome>;

export const DecideResponse = z.object({
  outcomes: z.array(DecisionOutcome),
});
export type DecideResponse = z.infer<typeof DecideResponse>;

// Resolve a flagged group: dissolve it, or keep it (clears the flag -> active).
export const ResolveFlagRequest = z
  .object({
    action: z.enum(['dissolve', 'keep']),
    note: z.string().max(500).optional(),
  })
  .strict();
export type ResolveFlagRequest = z.infer<typeof ResolveFlagRequest>;

export const ResolveFlagResponse = z.object({ group: Group });
export type ResolveFlagResponse = z.infer<typeof ResolveFlagResponse>;
